<template>
  <div class="control is-horizontal wide recall-times-control">
    <div class="control-label">
      <label-name labelKey="SITE-PWA-RECALL_TIMES"></label-name>
    </div>

    <div class="control recall-times-container">
      <!-- HEADER: UNIFIED SEGMENTED TABS (7 DAYS + WOCHENÜBERSICHT) -->
      <div class="day-tabs-header">
        <div class="day-tabs-container">
          <div class="day-tabs">
            <button
              v-for="day in weekDaysList"
              :key="day.key"
              type="button"
              class="day-tab-button"
              :class="{
                'is-active': activeDay === day.key && !showAllDaysView,
                'is-empty': !(daysData[day.key] && daysData[day.key].length > 0)
              }"
              @click="activeDay = day.key; showAllDaysView = false"
            >
              <span class="day-label">{{ day.label }}</span>
              <span
                v-if="daysData[day.key] && daysData[day.key].length > 0"
                class="tag is-rounded day-badge"
                :class="{
                  'is-active-badge': activeDay === day.key && !showAllDaysView,
                  'is-inactive-badge': activeDay !== day.key || showAllDaysView
                }"
              >
                {{ daysData[day.key].length }}
              </span>
              <span v-else class="tag is-rounded day-badge is-empty-badge">
                0
              </span>
            </button>

            <!-- 8TH TAB: WOCHENÜBERSICHT -->
            <div class="day-tab-divider"></div>
            <button
              type="button"
              class="day-tab-button overview-tab-button"
              :class="{ 'is-active': showAllDaysView }"
              @click="showAllDaysView = true"
              title="Wochenübersicht aller Rückruf-Zeiten anzeigen"
            >
              <span class="material-symbols-outlined tab-icon">calendar_view_week</span>
              <span class="overview-tab-text">Wochenübersicht</span>
            </button>
          </div>
        </div>
      </div>

      <!-- FULL WEEK OVERVIEW MODE -->
      <div v-if="showAllDaysView" class="all-days-overview">
        <div
          v-for="day in weekDaysList"
          :key="'overview-' + day.key"
          class="overview-day-card"
          :class="{ 'is-empty-card': !(daysData[day.key] && daysData[day.key].length > 0) }"
          @click="activeDay = day.key; showAllDaysView = false"
        >
          <div class="overview-day-header">
            <div class="overview-day-title">
              <strong>{{ day.label }}</strong>
              <span
                class="tag is-small day-status-badge"
                :class="daysData[day.key] && daysData[day.key].length > 0 ? 'is-success is-light' : 'is-light'"
              >
                {{ daysData[day.key] && daysData[day.key].length > 0 ? daysData[day.key].length + ' Zeitslot(s)' : 'Keine Rückrufe' }}
              </span>
            </div>
          </div>

          <div class="overview-slots-chips">
            <template v-if="daysData[day.key] && daysData[day.key].length > 0">
              <span
                v-for="(slot, sIdx) in daysData[day.key]"
                :key="sIdx"
                class="tag is-info is-light slot-chip"
              >
                {{ slot.from }} – {{ slot.to }} Uhr
              </span>
            </template>
            <span v-else class="has-text-grey is-size-7">
              Keine Rückruf-Zeiten für diesen Tag hinterlegt.
            </span>
          </div>
        </div>
      </div>

      <!-- SINGLE DAY EDITOR (ACTIVE DAY) -->
      <div v-else class="day-detail-card">
        <!-- DAY HEADER & STATUS -->
        <div class="day-card-header">
          <div class="day-title-wrap">
            <h4 class="day-heading mb-0">
              <span class="material-symbols-outlined">schedule</span>
              <span>{{ activeDay }}</span>
            </h4>
            <span
              class="tag day-status-badge"
              :class="activeDaySlots.length > 0 ? 'is-success is-light' : 'is-light'"
            >
              {{ activeDaySlots.length }} Rückruf-Zeitslot(s)
            </span>
          </div>

          <div class="day-header-actions">
            <button
              v-if="activeDaySlots.length > 1"
              type="button"
              class="button is-small is-light"
              @click="sortSlots(activeDay)"
              title="Zeitslots chronologisch sortieren"
            >
              <span class="material-symbols-outlined">sort</span>
              Sortieren
            </button>
          </div>
        </div>

        <!-- SLOTS LIST -->
        <div class="day-content">
          <!-- EMPTY STATE -->
          <div v-if="activeDaySlots.length === 0" class="empty-slots">
            <span class="material-symbols-outlined empty-state-icon">phone_callback</span>
            <p class="mb-2">Noch keine Rückruf-Zeitslots für <strong>{{ activeDay }}</strong> hinterlegt.</p>
            <div class="empty-slots-actions">
              <button
                type="button"
                class="button is-blue is-outline is-small"
                @click="addSlot(activeDay)"
              >
                <span class="material-symbols-outlined">add</span>
                Ersten Zeitslot anlegen
              </button>
              <button
                type="button"
                class="button is-light is-small"
                @click="generateDefaultSlots(activeDay)"
                title="Erstellt automatisch stündliche Slots von 08:00 bis 17:00"
              >
                <span class="material-symbols-outlined">auto_fix_high</span>
                Stündliche Standard-Slots (08:00 – 17:00)
              </button>
            </div>
          </div>

          <!-- SLOTS ROWS -->
          <div v-else class="slots-list">
            <div
              v-for="(slot, index) in activeDaySlots"
              :key="'slot-' + activeDay + '-' + index"
              class="slot-card"
            >
              <!-- 1. INDEX & REORDER HANDLE -->
              <div class="slot-handle">
                <span class="tag is-rounded is-light slot-index">#{{ index + 1 }}</span>
                <div class="reorder-buttons">
                  <button
                    type="button"
                    class="button is-small is-light btn-reorder"
                    :disabled="index === 0"
                    @click="moveUp(activeDay, index)"
                    title="Nach oben verschieben"
                  >
                    <span class="material-symbols-outlined">keyboard_arrow_up</span>
                  </button>
                  <button
                    type="button"
                    class="button is-small is-light btn-reorder"
                    :disabled="index === activeDaySlots.length - 1"
                    @click="moveDown(activeDay, index)"
                    title="Nach unten verschieben"
                  >
                    <span class="material-symbols-outlined">keyboard_arrow_down</span>
                  </button>
                </div>
              </div>

              <!-- 2. TIME INPUTS (FROM - TO) -->
              <div class="slot-time-fields">
                <span class="time-prefix">Von</span>
                <div class="select is-small time-select">
                  <select v-model="slot.from" @change="emitUpdate">
                    <option v-for="t in timeOptions" :key="'from-' + t.value" :value="t.value">
                      {{ t.name }}
                    </option>
                  </select>
                </div>

                <span class="time-separator">bis</span>

                <div class="select is-small time-select">
                  <select v-model="slot.to" @change="emitUpdate">
                    <option v-for="t in timeOptions" :key="'to-' + t.value" :value="t.value">
                      {{ t.name }}
                    </option>
                  </select>
                </div>

                <span class="time-suffix">Uhr</span>
              </div>

              <!-- 3. ACTIONS -->
              <div class="slot-actions">
                <button
                  type="button"
                  class="button is-small is-light is-icon-only btn-card-action"
                  title="Slot duplizieren"
                  @click="duplicateSlot(activeDay, index)"
                >
                  <span class="material-symbols-outlined">content_copy</span>
                </button>
                <button
                  type="button"
                  class="button is-small is-danger is-outline is-icon-only btn-card-action btn-delete"
                  title="Slot entfernen"
                  @click="removeSlot(activeDay, index)"
                >
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </div>
            </div>

            <!-- ADD ACTIONS UNDER SLOTS -->
            <div class="slot-add-actions">
              <button
                type="button"
                class="button is-blue is-outline is-small btn-add-slot"
                @click="addSlot(activeDay)"
              >
                <span class="material-symbols-outlined">add</span>
                Zeitslot hinzufügen
              </button>

              <button
                type="button"
                class="button is-light is-small"
                @click="generateDefaultSlots(activeDay)"
                title="Ergänzt stündliche Standard-Slots von 08:00 bis 17:00"
              >
                <span class="material-symbols-outlined">auto_fix_high</span>
                Standard-Slots (08:00 – 17:00)
              </button>

              <button
                type="button"
                class="button is-light is-danger is-outline is-small btn-clear-day ml-auto"
                @click="clearDay(activeDay)"
                title="Alle Slots für diesen Tag leeren"
              >
                <span class="material-symbols-outlined">delete_sweep</span>
                Tag leeren
              </button>
            </div>
          </div>

          <!-- BATCH ACTIONS / COPY TOOLS -->
          <div class="day-batch-actions">
            <div class="batch-title is-size-7 has-text-grey mb-2">
              <span class="material-symbols-outlined">bolt</span>
              <strong>Schnellfunktionen:</strong>
            </div>

            <div class="batch-buttons">
              <button
                type="button"
                class="button is-light btn-batch-action"
                @click="copyActiveDayToAllWeekdays"
                title="Überträgt alle Rückruf-Zeiten von diesem Tag auf Montag bis Freitag"
              >
                <span class="material-symbols-outlined">content_copy</span>
                Auf alle Werktage (Mo–Fr) übertragen
              </button>

              <div class="select is-small copy-target-select">
                <select v-model="targetCopyDay" @change="onCopyTargetSelect">
                  <option :value="''">Auf einzelnen Tag kopieren...</option>
                  <option
                    v-for="d in weekDaysList.filter(d => d.key !== activeDay)"
                    :key="'copy-to-' + d.key"
                    :value="d.key"
                  >
                    Nach {{ d.label }} kopieren
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- INFO HELP TEXT -->
      <span class="help is-info-text">
        Zeitfenster für den Rückrufservice der PWA. Format im Server-Payload: <code>HH:mm bis HH:mm</code>.
      </span>

      <!-- FOOTER: RAW JSON PANEL -->
      <pharmacy-raw-json-panel
        :json-data="serializeData()"
        title="JSON-Rohdaten (Rückruf-Zeiten)"
        :rows="12"
        placeholder='{\n  "Montag": [\n    "08:00 bis 09:00",\n    "15:00 bis 16:00"\n  ]\n}'
        @apply="onApplyRawJson"
      />
    </div>
  </div>
</template>

<script>
import PharmacyRawJsonPanel from './incl-pharmacy-raw-json-panel.vue';
const GERMAN_WEEKDAYS = [
  { key: 'Montag', label: 'Montag', short: 'Mo' },
  { key: 'Dienstag', label: 'Dienstag', short: 'Di' },
  { key: 'Mittwoch', label: 'Mittwoch', short: 'Mi' },
  { key: 'Donnerstag', label: 'Donnerstag', short: 'Do' },
  { key: 'Freitag', label: 'Freitag', short: 'Fr' },
  { key: 'Samstag', label: 'Samstag', short: 'Sa' },
  { key: 'Sonntag', label: 'Sonntag', short: 'So' }
];

function generateTimeOptions() {
  const options = [];
  for (let h = 0; h < 24; h++) {
    const hh = String(h).padStart(2, '0');
    for (const m of ['00', '15', '30', '45']) {
      options.push({
        value: `${hh}:${m}`,
        name: `${hh}:${m}`
      });
    }
  }
  return options;
}

const BASE_TIME_OPTIONS = generateTimeOptions();

function parseSlotString(str) {
  if (typeof str !== 'string') return { from: '08:00', to: '09:00' };
  const trimmed = str.trim();
  const match = trimmed.match(/^(\d{1,2}:\d{2})\s*(?:zw\.?|-|bis)\s*(\d{1,2}:\d{2})$/i);
  if (match) {
    const formatTime = t => {
      const parts = t.split(':');
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    };
    return {
      from: formatTime(match[1]),
      to: formatTime(match[2])
    };
  }
  return { from: '08:00', to: '09:00', raw: trimmed };
}

export default {
  name: 'PharmacyRecallTimes',

  components: {
    PharmacyRawJsonPanel
  },

  props: {
    // Key-value object { "Montag": ["08:00 zw. 09:00"] } or JSON string
    value: {
      type: [Object, String],
      default: () => ({})
    }
  },

  data() {
    return {
      weekDaysList: GERMAN_WEEKDAYS,
      activeDay: 'Montag',
      showAllDaysView: false,
      daysData: this.parseValue(this.value),
      targetCopyDay: '',
      isInternalUpdate: false
    };
  },

  computed: {
    activeDaySlots() {
      if (!this.daysData[this.activeDay]) {
        this.$set(this.daysData, this.activeDay, []);
      }
      return this.daysData[this.activeDay];
    },

    timeOptions() {
      const options = [...BASE_TIME_OPTIONS];
      const customTimes = new Set();

      Object.values(this.daysData).forEach(slots => {
        (slots || []).forEach(slot => {
          if (slot && slot.from) customTimes.add(slot.from);
          if (slot && slot.to) customTimes.add(slot.to);
        });
      });

      customTimes.forEach(ct => {
        if (ct && !options.some(o => o.value === ct)) {
          options.push({ value: ct, name: ct });
        }
      });

      return options.sort((a, b) => a.value.localeCompare(b.value));
    }
  },

  watch: {
    value: {
      deep: true,
      handler(newVal) {
        if (this.isInternalUpdate) {
          this.isInternalUpdate = false;
          return;
        }
        this.daysData = this.parseValue(newVal);
      }
    }
  },

  methods: {
    parseValue(val) {
      let data = val;
      if (typeof val === 'string' && val.trim() !== '') {
        try {
          data = JSON.parse(val);
        } catch (e) {
          data = {};
        }
      }
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        data = {};
      }

      const result = {};
      GERMAN_WEEKDAYS.forEach(day => {
        const slots = data[day.key] || [];
        result[day.key] = Array.isArray(slots)
          ? slots.map(parseSlotString)
          : [];
      });
      return result;
    },

    serializeData() {
      const result = {};
      GERMAN_WEEKDAYS.forEach(day => {
        const slots = this.daysData[day.key] || [];
        const serializedSlots = slots
          .filter(s => s && s.from && s.to)
          .map(s => `${s.from} bis ${s.to}`);
        if (serializedSlots.length > 0) {
          result[day.key] = serializedSlots;
        }
      });
      return result;
    },

    emitUpdate() {
      const payload = this.serializeData();
      this.isInternalUpdate = true;
      this.$emit('input', payload);
      this.$emit('change', payload);

      if (this.showRawJson) {
        this.rawJsonInput = JSON.stringify(payload, null, 2);
      }
    },

    addSlot(dayKey) {
      const slots = this.daysData[dayKey] || [];
      let nextFrom = '08:00';
      let nextTo = '09:00';

      if (slots.length > 0) {
        const lastSlot = slots[slots.length - 1];
        if (lastSlot && lastSlot.to) {
          nextFrom = lastSlot.to;
          const [h, m] = nextFrom.split(':').map(Number);
          const nextH = Math.min(h + 1, 23);
          nextTo = `${String(nextH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        }
      }

      slots.push({ from: nextFrom, to: nextTo });
      this.emitUpdate();
    },

    generateDefaultSlots(dayKey) {
      const defaultSlots = [
        { from: '08:00', to: '09:00' },
        { from: '09:00', to: '10:00' },
        { from: '10:00', to: '11:00' },
        { from: '11:00', to: '12:00' },
        { from: '12:00', to: '13:00' },
        { from: '13:00', to: '14:00' },
        { from: '14:00', to: '15:00' },
        { from: '15:00', to: '16:00' },
        { from: '16:00', to: '17:00' }
      ];
      this.$set(this.daysData, dayKey, defaultSlots);
      this.emitUpdate();
    },

    removeSlot(dayKey, index) {
      this.daysData[dayKey].splice(index, 1);
      this.emitUpdate();
    },

    duplicateSlot(dayKey, index) {
      const source = this.daysData[dayKey][index];
      const clone = { from: source.from, to: source.to };
      this.daysData[dayKey].splice(index + 1, 0, clone);
      this.emitUpdate();
    },

    moveUp(dayKey, index) {
      if (index <= 0) return;
      const item = this.daysData[dayKey].splice(index, 1)[0];
      this.daysData[dayKey].splice(index - 1, 0, item);
      this.emitUpdate();
    },

    moveDown(dayKey, index) {
      if (index >= this.daysData[dayKey].length - 1) return;
      const item = this.daysData[dayKey].splice(index, 1)[0];
      this.daysData[dayKey].splice(index + 1, 0, item);
      this.emitUpdate();
    },

    sortSlots(dayKey) {
      if (!this.daysData[dayKey]) return;
      this.daysData[dayKey].sort((a, b) => (a.from || '').localeCompare(b.from || ''));
      this.emitUpdate();
    },

    clearDay(dayKey) {
      this.$set(this.daysData, dayKey, []);
      this.emitUpdate();
    },

    copyActiveDayToAllWeekdays() {
      const sourceSlots = this.daysData[this.activeDay] || [];
      ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'].forEach(day => {
        if (day !== this.activeDay) {
          this.$set(
            this.daysData,
            day,
            sourceSlots.map(s => ({ from: s.from, to: s.to }))
          );
        }
      });
      this.emitUpdate();
    },

    onCopyTargetSelect() {
      if (!this.targetCopyDay) return;
      const sourceSlots = this.daysData[this.activeDay] || [];
      this.$set(
        this.daysData,
        this.targetCopyDay,
        sourceSlots.map(s => ({ from: s.from, to: s.to }))
      );
      this.targetCopyDay = '';
      this.emitUpdate();
    },

    onApplyRawJson(parsed, done) {
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return done('Das JSON muss ein Objekt mit Wochentagen als Schlüssel sein (z.B. { "Montag": ["08:00 bis 09:00"] }).');
      }
      this.daysData = this.parseValue(parsed);
      this.emitUpdate();
      done();
    }
  }
};
</script>

<style lang="scss" scoped>
.recall-times-control {
  margin-top: 14px;
  margin-bottom: 24px;
  align-items: flex-start;

  > .control-label {
    padding-top: 6px;
  }
}

.recall-times-container {
  flex: 1;
  max-width: 950px;
  width: 100%;
}

/* DAY TABS HEADER */
.day-tabs-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e6e9ec;
}

.day-tabs-container {
  display: inline-flex;
  align-items: center;
  background-color: #eaedf1;
  border: 1px solid #d5dbe1;
  border-radius: 8px;
  padding: 3px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
}

.day-tabs {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.day-tab-divider {
  width: 1px;
  height: 20px;
  background-color: #cbd5e1;
  margin: 0 4px;
}

.day-tab-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
  line-height: 1.2;

  &:hover:not(.is-active) {
    background-color: rgba(255, 255, 255, 0.6);
    color: #1e293b;
  }

  &.is-active {
    background-color: #ffffff;
    color: #2780e3;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &.is-empty:not(.is-active) {
    color: #8392a5;
  }

  .day-badge {
    font-size: 10px;
    height: 18px;
    min-width: 18px;
    padding: 0 5px;
    font-weight: 600;
    line-height: 18px;

    &.is-active-badge {
      background-color: #2780e3;
      color: #fff;
    }

    &.is-inactive-badge {
      background-color: #dbeafe;
      color: #1e40af;
    }

    &.is-empty-badge {
      background-color: #e2e8f0;
      color: #94a3b8;
    }
  }

  .tab-icon {
    font-size: 17px;
    vertical-align: middle;
  }

  .overview-tab-text {
    font-size: 12px;
  }
}

/* DAY DETAIL CARD */
.day-detail-card {
  background-color: #fff;
  border: 1px solid #e1e6eb;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  margin-bottom: 12px;

  .day-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #eef2f5;
    background-color: #fbfcfd;

    .day-title-wrap {
      display: inline-flex;
      align-items: center;
      gap: 10px;

      .day-heading {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 16px;
        font-weight: 600;
        color: #2c3e50;
        margin: 0 !important;
        padding: 0 !important;
        line-height: 1 !important;

        .material-symbols-outlined {
          font-size: 20px;
          color: #2780e3;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          margin: 0;
        }
      }

      .day-status-badge {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin: 0 !important;
        font-size: 11px !important;
        height: 22px !important;
        padding: 0 8px !important;
        line-height: 22px !important;
        vertical-align: middle !important;
      }
    }
  }

  .day-content {
    padding: 14px 16px;
  }
}

/* SLOTS LIST */
.slots-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 520px;
}

.slot-card {
  display: flex;
  align-items: center;
  background-color: #fff;
  border: 1px solid #e1e6eb;
  border-radius: 6px;
  padding: 8px 12px;
  gap: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  transition: border-color 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
  }
}

/* HANDLE */
.slot-handle {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 56px;
  width: 56px;

  .slot-index {
    font-size: 11px;
    font-weight: 700;
    color: #555;
    background-color: #f0f3f6;
    padding: 0 6px;
    height: 20px;
    line-height: 20px;
  }

  .reorder-buttons {
    display: flex;
    flex-direction: column;
    gap: 1px;

    .btn-reorder {
      width: 20px !important;
      height: 16px !important;
      min-width: 20px !important;
      padding: 0 !important;
      border: none !important;
      background: transparent !important;
      color: #7a7a7a !important;
      cursor: pointer;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      line-height: 1 !important;

      .material-symbols-outlined {
        margin: 0 !important;
        padding: 0 !important;
        font-size: 15px !important;
        line-height: 1 !important;
      }

      &:hover:not(:disabled) {
        color: #2780e3 !important;
        background-color: #f0f4f8 !important;
        border-radius: 2px !important;
      }

      &:disabled {
        opacity: 0.3 !important;
        cursor: not-allowed;
      }
    }
  }
}

/* TIME INPUTS */
.slot-time-fields {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;

  .time-prefix,
  .time-separator,
  .time-suffix {
    font-size: 13px;
    color: #64748b;
  }

  .time-select {
    height: 32px;

    select {
      height: 32px;
      font-size: 13px;
      width: 95px;
      min-width: 95px;
      padding-left: 8px;
      padding-right: 24px;
      border-radius: 3px;
      border-color: #dbdbdb;

      &:focus {
        border-color: #45a3d2;
      }
    }

    &::after {
      right: 10px;
    }
  }
}

/* ACTIONS */
.slot-actions {
  display: flex;
  align-items: center;
  gap: 4px;

  .button.is-icon-only,
  .btn-card-action {
    height: 28px !important;
    width: 28px !important;
    min-width: 28px !important;
    border-radius: 4px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;
    line-height: 1 !important;

    .material-symbols-outlined,
    .icon,
    span {
      margin: 0 !important;
      margin-right: 0 !important;
      margin-left: 0 !important;
      padding: 0 !important;
      font-size: 15px !important;
      line-height: 1 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;
    }

    &.btn-delete {
      color: #fff !important;
      background-color: #e74c3c !important;
      border-color: #e74c3c !important;

      &:hover {
        background-color: #c0392b !important;
        border-color: #c0392b !important;
      }
    }
  }
}

/* ADD SLOTS ACTIONS */
.slot-add-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  padding-top: 6px;

  .btn-add-slot {
    height: 28px;
    font-size: 12px;
    border-radius: 4px;

    .material-symbols-outlined {
      font-size: 16px;
      margin-right: 4px;
    }
  }
}

/* BATCH ACTIONS */
.day-batch-actions {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;

  .batch-title {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #64748b;

    .material-symbols-outlined {
      font-size: 14px;
      color: #f59e0b;
    }
  }

  .batch-buttons {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .btn-batch-action {
      height: 28px;
      font-size: 12px;
      color: #475569;
      border-radius: 4px;

      .material-symbols-outlined {
        font-size: 15px;
        margin-right: 4px;
      }

      &:hover {
        color: #2780e3;
      }
    }

    .copy-target-select {
      height: 28px;
      select {
        height: 28px;
        font-size: 12px;
        padding-left: 8px;
      }
    }
  }
}

/* EMPTY SLOTS */
.empty-slots {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: #fafbfc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  text-align: center;
  font-size: 13px;

  .empty-state-icon {
    font-size: 32px;
    color: #94a3b8;
    margin-bottom: 6px;
  }

  .empty-slots-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 8px;

    .button {
      margin: 0 !important;
    }
  }
}

/* ALL DAYS OVERVIEW */
.all-days-overview {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  margin-bottom: 12px;

  .overview-day-card {
    background-color: #fff;
    border: 1px solid #e1e6eb;
    border-radius: 6px;
    padding: 12px 14px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: #2780e3;
      box-shadow: 0 2px 6px rgba(39, 128, 227, 0.12);
    }

    &.is-empty-card {
      background-color: #fafbfc;
      opacity: 0.8;
    }

    .overview-day-header {
      margin-bottom: 8px;

      .overview-day-title {
        display: flex;
        justify-content: space-between;
        align-items: center;

        strong {
          font-size: 14px;
          color: #1e293b;
        }
      }
    }

    .overview-slots-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;

      .slot-chip {
        font-size: 11px;
        height: 22px;
      }
    }
  }
}

.help.is-info-text {
  display: block;
  font-size: 11px;
  color: #7a7a7a;
  margin-top: 8px;
  margin-bottom: 0;
  line-height: 1.4;
}

</style>

